import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faTimes, faUser, faSignOutAlt } from '@fortawesome/free-solid-svg-icons';
import Weather from './Weather';
import LanguageSelector from './LanguageSelector';
import ExchangeRate from './ExchangeRate';
import AuthModal from './AuthModal';
import ConfirmationModal from './ConfirmationModal';
import { ReactComponent as QQIcon } from '../resources/QQ-Icon.svg';
import { useLanguage } from '../contexts/LanguageContext';
import { translations } from '../translations';
import config from '../config/config';

const NavbarContainer = styled.nav`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 16px;
  height: 88px;
  width: 100%;
  gap: 24px;
  position: relative;

  @media (max-width: 768px) {
    padding: 16px 8px;
    gap: 12px;
  }
`;

const LeftSection = styled.div`
  flex: 0 0 auto;
  margin-left: 16px;
  background: white;
  border-radius: 32px;
  display: flex;
  align-items: center;
  padding: 0 24px;
  height: 48px;
  min-width: fit-content;

  @media (max-width: 1201px) {
    padding: 0 8px;
    min-width: fit-content;
    margin-left: 8px;
  }
`;

const MinimalLanguagePill = styled.div`
  flex: 0 0 auto;
  margin-right: 16px;
  background: transparent;
  border-radius: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 8px;
  height: 48px;

  > div {
    margin: 0;
  }
  img {
    width: 32px;
    height: 32px;
  }

  @media (max-width: 1201px) {
    margin-right: 8px;
    padding: 0 6px;
  }
`;

const RightSection = styled.div`
  flex: 0 0 auto;
  background: #14464F;
  border-radius: 46px;
  opacity: 1;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 16px;
  margin-right: 16px;
  height: 48px;
  min-width: ${props => props.$minimal ? 'auto' : '200px'};

  @media (max-width: 1140px) {
    padding: ${props => props.$minimal ? '8px 16px' : '4px'};
    width: ${props => props.$minimal ? 'auto' : '48px'};
    min-width: auto;
    justify-content: center;
    align-items: center;
    margin-right: 8px;
  }
`;

const LogoIcon = styled(QQIcon)`
  width: 48px;
  height: 32px;
  image-rendering: -webkit-optimize-contrast;
  image-rendering: crisp-edges;
  shape-rendering: geometricPrecision;

  @media (max-width: 768px) {
    width: 48px;
    height: 48px;
  }
`;

const LogoText = styled.span`
  @media (max-width: 1201px) {
    display: none;
  }
`;

const Logo = styled(Link)`
  font-family: 'Josefin Sans';
  font-weight: 700;
  font-size: 24px;
  color: #14464F;
  text-decoration: none;
  letter-spacing: 0.5px;
  display: flex;
  align-items: center;
  gap: 6px;
  padding-right: 0;
`;

const WidgetsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  height: 100%;

  @media (max-width: 1200px) {
    display: none;
  }
`;

const RightAlignedItems = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  height: 100%;

  ${props =>
    props.$minimal &&
    `
    flex-wrap: nowrap;
    align-items: center;
    gap: 12px;
  `}

  @media (max-width: 1200px) {
    .language-selector {
      display: ${props => props.$minimal ? 'flex' : 'none'};
      align-items: center;
    }
  }
`;

const OperatorLogoutButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  padding: 0;
  border: none;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.15);
  color: #ffffff;
  cursor: pointer;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(255, 255, 255, 0.28);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const MenuButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 24px;
  cursor: pointer;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  width: 32px;
  margin-left: 8px;

  @media (max-width: 1140px) {
    margin-left: 0;
    width: 32px;
    height: 32px;
  }

  &:hover {
    opacity: 0.8;
  }
`;

const Menu = styled.div`
  position: absolute;
  top: 80px;
  right: 16px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  padding: 8px;
  z-index: 1500;
  min-width: 200px;
`;

const MenuItem = styled(({ isButton, ...props }) => 
  isButton ? <button {...props} /> : <Link {...props} />
)`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  color: #333;
  text-decoration: none;
  border-radius: 8px;
  gap: 12px;
  width: 100%;
  background: none;
  border: none;
  cursor: pointer;
  font-size: inherit;
  text-align: left;

  &:hover {
    background: #f5f5f5;
  }

  svg {
    width: 16px;
  }
`;

const MenuDivider = styled.div`
  height: 1px;
  background: #eee;
  margin: 8px 0;
`;

const CenterSection = styled.div`
  flex: 1 1 auto;
  background: rgba(255, 255, 255, 0.3);
  box-shadow: inset 1px 1px 8px rgba(255, 255, 255, 0.64);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 32px;
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  overflow-x: auto;
  min-width: 200px;
  white-space: nowrap;
  scrollbar-width: none;
  -ms-overflow-style: none;
  height: 48px;
  padding: 0 8px;
  
  &::-webkit-scrollbar {
    display: none;
  }

  @media (max-width: 768px) {
    min-width: 150px;
    padding: 0 4px;
  }
`;

const FilterContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  margin: 0 auto;
  width: fit-content;
  min-width: min-content;

  ${CenterSection}:hover & {
    margin: ${props => props.isOverflowing ? '0' : '0 auto'};
  }
`;

const FilterItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  color: #14464F;
  cursor: pointer;
  transition: all 0.3s ease;
  border-radius: 20px;
  background: ${props => props.active ? 'white' : 'transparent'};
  font-size: 13px;
  white-space: nowrap;
  flex-shrink: 0;
  height: 36px;

  &:first-child {
    margin-left: 4px;
  }

  &:last-child {
    margin-right: 4px;
  }

  &:hover {
    color: #14464F;
    background: ${props => props.active ? 'white' : 'rgba(255, 255, 255, 0.5)'};
  }

  img {
    width: 16px;
    height: 16px;
    object-fit: contain;
    filter: ${props => props.active ? 'brightness(0) saturate(100%) invert(56%) sepia(93%) saturate(385%) hue-rotate(334deg) brightness(97%) contrast(94%)' : 'brightness(0) saturate(100%) invert(24%) sepia(29%) saturate(1014%) hue-rotate(154deg) brightness(94%) contrast(89%)'};
  }

  @media (max-width: 768px) {
    padding: 4px 8px;
    font-size: 11px;
    gap: 4px;
    height: 32px;
    
    img {
      width: 14px;
      height: 14px;
    }
  }
`;

const LanguageContainer = styled.div`
  /* Top padding nudged language down vs. weather row; skip in operator bar */
  padding-top: ${props => (props.$operatorBar ? 0 : '5px')};
  height: 100%;
  display: flex;
  align-items: center;
  /* Operator: same 40px row height as logout control — inner LanguageSelector is flush 40×40 */
  ${props =>
    props.$operatorBar &&
    `
    height: 40px;
    align-items: center;
  `}
`;

const MenuWidgets = styled.div`
  display: none;
  padding: 12px 16px;
  border-bottom: 1px solid #eee;

  @media (max-width: 1140px) {
    display: flex;
    flex-direction: row;
    justify-content: space-around;
    align-items: center;
    width: 100%;
    padding: 12px 8px;
    gap: 8px;

    .weather-widget, 
    .exchange-widget,
    .language-widget {
      color: #14464F;
      flex: 0 1 auto;
      display: flex;
      justify-content: center;
      align-items: center;
      
      svg, 
      button,
      select {
        color: #14464F;
      }
    }

    .exchange-widget {
      margin-left: -30px;
    }
  }
`;

const Navbar = ({ onFilterChange, activeFilter = null, minimal = false, variant = 'default' }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [activePopup, setActivePopup] = useState(null);
  const filterContainerRef = React.useRef(null);
  const centerSectionRef = React.useRef(null);
  const location = useLocation();
  const isHomePage = location.pathname === '/' || location.pathname === '/explore';
  const [amenities, setAmenities] = useState([]);
  const { language } = useLanguage();
  const isOperatorNav = variant === 'operator';
  const isAuthenticated = false;
  const brandingPath = '/welcome';

  useEffect(() => {
    if (minimal || !isHomePage) return;

    const fetchAmenities = async () => {
      try {
        const response = await fetch(`${config.API_URL}/general/amenities`);
        const data = await response.json();
        setAmenities(data);
      } catch (error) {
        console.error('Error fetching amenities:', error);
      }
    };

    fetchAmenities();
  }, [minimal, isHomePage]);

  useEffect(() => {
    const checkOverflow = () => {
      if (filterContainerRef.current && centerSectionRef.current) {
        const isContentOverflowing = filterContainerRef.current.scrollWidth > centerSectionRef.current.clientWidth;
        setIsOverflowing(isContentOverflowing);
      }
    };

    checkOverflow();
    window.addEventListener('resize', checkOverflow);
    return () => window.removeEventListener('resize', checkOverflow);
  }, [amenities]);

  const handleLogoutClick = () => {
    setShowLogoutConfirm(true);
    setIsMenuOpen(false);
  };

  const handleLogoutConfirm = () => {
    setShowLogoutConfirm(false);
  };

  const handleLoginClick = () => {
    setShowAuthModal(true);
    setIsMenuOpen(false);
  };

  const handleAuthSuccess = () => {
    setShowAuthModal(false);
  };

  const handleFilterClick = (value) => {
    if (onFilterChange) {
      // Radio button behavior: only one filter can be selected at a time
      // Don't allow unselecting the current filter
      if (activeFilter !== value) {
        onFilterChange(value);
      }
    }
  };

  const handlePopupToggle = (popupName) => {
    if (popupName === 'menu') {
      setIsMenuOpen(!isMenuOpen);
    } else if (popupName === 'language') {
      // For language selector, don't close the mobile menu
      setActivePopup(activePopup === popupName ? null : popupName);
      return;
    } else {
      setIsMenuOpen(false);
    }
    setActivePopup(activePopup === popupName ? null : popupName);
    if (popupName !== 'auth') setShowAuthModal(false);
  };

  return (
    <NavbarContainer>
      <LeftSection>
        <Logo to={brandingPath}>
          <LogoIcon />
          <LogoText>Quisqueya Quest</LogoText>
        </Logo>
      </LeftSection>

      {isHomePage && (
        <CenterSection ref={centerSectionRef}>
          <FilterContainer ref={filterContainerRef} isOverflowing={isOverflowing}>
            {amenities.map(amenity => (
              <FilterItem
                key={amenity.id}
                active={activeFilter === amenity.id}
                onClick={() => handleFilterClick(amenity.id)}
              >
                <img src={amenity.icon} alt={language === 'es' ? amenity.name_es : amenity.name} />
                {language === 'es' ? amenity.name_es : amenity.name}
              </FilterItem>
            ))}
          </FilterContainer>
        </CenterSection>
      )}

      {isOperatorNav ? (
        <RightSection $minimal>
          <RightAlignedItems $minimal>
            <LanguageContainer className="language-selector" $operatorBar>
              <LanguageSelector
                onPopupToggle={() => handlePopupToggle('language')}
                isOpen={activePopup === 'language'}
                highResFlags
                flushChrome
              />
            </LanguageContainer>
            {isAuthenticated && (
              <OperatorLogoutButton
                type="button"
                onClick={handleLogoutClick}
                title={translations[language].navbar.logout}
                aria-label={translations[language].navbar.logout}
              >
                <FontAwesomeIcon icon={faSignOutAlt} />
              </OperatorLogoutButton>
            )}
          </RightAlignedItems>
        </RightSection>
      ) : minimal ? (
        <MinimalLanguagePill>
          <LanguageSelector onPopupToggle={() => handlePopupToggle('language')} isOpen={activePopup === 'language'} />
        </MinimalLanguagePill>
      ) : (
        <RightSection>
          <WidgetsContainer>
            <Weather />
            <ExchangeRate onPopupToggle={() => handlePopupToggle('exchange')} isOpen={activePopup === 'exchange'} />
          </WidgetsContainer>
          <RightAlignedItems>
            <LanguageContainer className="language-selector">
              <LanguageSelector onPopupToggle={() => handlePopupToggle('language')} isOpen={activePopup === 'language'} />
            </LanguageContainer>
            <MenuButton onClick={() => handlePopupToggle('menu')}>
              <FontAwesomeIcon icon={isMenuOpen ? faTimes : faBars} />
            </MenuButton>
          </RightAlignedItems>
        </RightSection>
      )}

      {!isOperatorNav && isMenuOpen && (
        <Menu>
          <MenuWidgets>
            <div className="weather-widget">
              <Weather />
            </div>
            <div className="exchange-widget">
              <ExchangeRate />
            </div>
            <div className="language-widget">
              <LanguageSelector onPopupToggle={() => handlePopupToggle('language')} isOpen={activePopup === 'language'} />
            </div>
          </MenuWidgets>

          {isAuthenticated ? (
            <>
              <MenuItem isButton onClick={handleLogoutClick}>
                <FontAwesomeIcon icon={faSignOutAlt} />
                {translations[language].navbar.logout}
              </MenuItem>
            </>
          ) : (
            <>
              <MenuItem isButton onClick={handleLoginClick}>
                <FontAwesomeIcon icon={faUser} />
                {translations[language].navbar.login}
              </MenuItem>
            </>
          )}
        </Menu>
      )}

      {showAuthModal && (
        <AuthModal 
          onClose={() => setShowAuthModal(false)} 
          onAuthSuccess={handleAuthSuccess}
        />
      )}

      {showLogoutConfirm && (
        <ConfirmationModal
          isOpen={showLogoutConfirm}
          onClose={() => setShowLogoutConfirm(false)}
          onConfirm={handleLogoutConfirm}
          title={translations[language].navbar.confirmLogout.title}
          message={translations[language].navbar.confirmLogout.message}
          confirmText={translations[language].navbar.logout}
          cancelText={translations[language].common.cancel}
        />
      )}
    </NavbarContainer>
  );
};

export default Navbar;
